import React, { useEffect, useState, useRef } from "react";
import "./Home.scss";
import Header from "../components/Header";
import HeroSection from "../components/HeroSection";
import WhyCograd from "../components/WhyCograd";
import ImpactSection from "../components/ImpactSection";
import PopupModal from "../components/PopupModal";
import InnovationSection from "../components/InnovationSection";
import IncubationBanner from "../components/IncubationBanner";
import HackathonBanner from "../components/HackathonBanner";
import YoungFoundersBanner from "../components/YoungFoundersBanner";
import FeaturedIn from "../components/FeaturedIn";
import MedhaAISection from "../components/MedhaAISection";
import MedhaTeachers from "../components/MedhaTeachers";
import NitiFeature from "../components/NitiFeature";
import MouCollaborations from "../components/MouCollaborations";
import AppSuiteSection from "../components/AppSuiteSection";
import WorkedWithVelocity from "../components/WorkedWithVelocity";

// Gallery Images
import sti1 from "../assets/STI/sti1.webp";
import sti2 from "../assets/STI/sti2.webp";
import sti3 from "../assets/STI/sti3.webp";
import sti4 from "../assets/STI/sti4.webp";
import sti5 from "../assets/STI/sti5.webp";
import sti6 from "../assets/STI/sti6.webp";
import sti7 from "../assets/STI/sti7.webp";
import sti8 from "../assets/STI/sti8.webp";

import Projects from "../components/Projects";
import SuccessStories from "../components/SuccessStories";
import Testimonials from "../components/Testimonials";
import AwardsCarousel from "../components/AwardsCarousel";
import LeadershipTeam from "../components/LeadershipTeam";
import WeblinearBentoGallery from "../components/WeblinearBentoGalleryMarquee";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet";
import { CountUp } from "countup.js";
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ArrowUpRight } from 'iconoir-react';
import { Target, Cpu, BarChart2, Trophy } from 'lucide-react';

const mediaItems = [
  { id: 1, type: 'image', images: [sti1] },
  { id: 2, type: 'image', images: [sti2] },
  { id: 3, type: 'image', images: [sti3] },
  { id: 4, type: 'image', images: [sti4] },
  { id: 5, type: 'image', images: [sti5] },
  { id: 6, type: 'image', images: [sti6] },
  { id: 7, type: 'image', images: [sti7] },
  { id: 8, type: 'image', images: [sti8] },
];

const Home = () => {
  const [showPopup, setShowPopup] = useState(false);
  const hasSeenPopup = useRef(false);

  useEffect(() => {
    const popupSeen = sessionStorage.getItem('cogradPopupSeen');
    if (popupSeen || hasSeenPopup.current) return;

    const onScroll = () => {
      if (window.scrollY > window.innerHeight * 0.6) {
        setShowPopup(true);
        hasSeenPopup.current = true;
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    const options = { duration: 5, separator: "," };
    const updateWithPlusSign = (id, value) => {
      const countUp = new CountUp(id, value, options);
      if (!countUp.error) {
        countUp.start(() => {
          const el = document.getElementById(id);
          if (el) el.innerHTML += "+";
        });
      }
    };
    updateWithPlusSign("workshops", 5000);
    updateWithPlusSign("students", 300000);
    updateWithPlusSign("successStories", 2000);
    updateWithPlusSign("schools", 1700);
  }, []);

  const handleButtonClick = () => {
    window.location.href = "/contact-us";
  };

  return (
    <div className="home">
      <Helmet>
        <title>Cograd - Innovative and Affordable Education</title>
        <meta
          name="description"
          content="Cograd offers innovative and affordable education solutions. Discover our vision for accessible education, services, and partnership opportunities. Learn more about Cograd now."
        />
        <link rel="canonical" href="https://cograd.in/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="CoGrad" />
        <meta property="og:title" content="Cograd - Innovative and Affordable Education" />
        <meta
          property="og:description"
          content="Cograd is dedicated to building innovative and affordable education solutions for schools across India."
        />
        <meta property="og:url" content="https://cograd.in/" />
        <meta property="og:image" content="https://cograd.in/logo512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cograd - Innovative and Affordable Education" />
        <meta
          name="twitter:description"
          content="Cograd is dedicated to building innovative and affordable education solutions for schools across India."
        />
        <meta name="twitter:image" content="https://cograd.in/logo512.png" />
      </Helmet>
      <Header />
      <div className="hero-above-fold">
        <HeroSection />
        <FeaturedIn />
      </div>
      {/* While Young Founders entries are open this leads, since it is the
          time-bound ask; it removes itself on the 31st and YUVASETU returns to
          sitting directly under the hero. */}
      <YoungFoundersBanner />
      <HackathonBanner />
      <MouCollaborations />

      <IncubationBanner />
      <MedhaAISection />
      <MedhaTeachers />
      <NitiFeature />
      <AppSuiteSection />
      <WhyCograd />
      <PopupModal isOpen={showPopup} onClose={handleClosePopup} />
      <InnovationSection />
      <ImpactSection />
      <WorkedWithVelocity />
      <Projects />

      <SuccessStories />
      <AwardsCarousel />
      <LeadershipTeam />
      <Testimonials />
      <div id="shark-tank">
        <WeblinearBentoGallery
          mediaItems={mediaItems}
          title="As Seen on Shark Tank India"
          description=""
        />
      </div>

      <section className="upgrade-cta">
        <div className="upgrade-cta__card">
          <h2 className="upgrade-cta__h2">Ready to Upgrade<br />Your School?</h2>
          <p className="upgrade-cta__sub">
            Innovative solutions to elevate your institution with Cograd — cutting-edge
            technology, personalized support, and tailored programs built for India.
          </p>
          <div className="upgrade-cta__feats">
            <span className="upgrade-cta__feat"><Target size={13} strokeWidth={2.5} />NEP-Aligned</span>
            <span className="upgrade-cta__feat"><Cpu size={13} strokeWidth={2.5} />AI-Powered</span>
            <span className="upgrade-cta__feat"><BarChart2 size={13} strokeWidth={2.5} />Live Analytics</span>
            <span className="upgrade-cta__feat"><Trophy size={13} strokeWidth={2.5} />Proven Results</span>
          </div>
          <button className="upgrade-cta__btn" onClick={handleButtonClick}>
            Upgrade Now <ArrowUpRight />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
